/**
 * Shared page-1 fetch for MarketSync report domains (returns, FBA, S&T, …).
 */
import {
  amazonConnectApi,
  type AcquisitionReportPage,
} from '../api/amazonconnect';

const TTL_MS = 45_000;
const cache = new Map<string, { at: number; data: AcquisitionReportPage }>();
const inflight = new Map<string, Promise<AcquisitionReportPage>>();

const empty = (domain: string): AcquisitionReportPage => ({
  domain,
  items: [],
  count: 0,
  total_count: 0,
  page: 1,
  page_size: 40,
  pages: 0,
  has_more: false,
});

export async function fetchReportPage(
  connectionId: string,
  domain: string,
): Promise<AcquisitionReportPage> {
  const key = `${connectionId}|${domain}|1`;
  const hit = cache.get(key);
  if (hit && Date.now() - hit.at < TTL_MS) return hit.data;
  const pending = inflight.get(key);
  if (pending) return pending;
  const job = amazonConnectApi
    .listAcquisitionReports(connectionId, domain, { page: 1, page_size: 40 })
    .then((data) => {
      const page: AcquisitionReportPage = {
        ...empty(domain),
        ...data,
        items: data.items ?? [],
      };
      cache.set(key, { at: Date.now(), data: page });
      return page;
    })
    .catch(() => empty(domain))
    .finally(() => {
      inflight.delete(key);
    });
  inflight.set(key, job);
  return job;
}

export async function fetchReportPages(
  connectionId: string,
  domains: string[],
): Promise<Record<string, AcquisitionReportPage>> {
  const entries = await Promise.all(
    domains.map(async (d) => [d, await fetchReportPage(connectionId, d)] as const),
  );
  return Object.fromEntries(entries);
}
